import { type EntityKind, matchEntityRoute } from "@/components/entity-preview/entity-kinds";
import type { IconName } from "@/components/icons/Icon";
import { getProjectNavigationRouteKey } from "@/components/navigation/project-navigation-route-key";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { useLocalStorageSyncedState } from "@/utils/hooks/use-local-storage-synced-state";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";

export const PROJECT_NAV_HISTORY_STORED_LIMIT = 20;
export const PROJECT_NAV_HISTORY_SHOWN_LIMIT = 8;

const ENVIRONMENT_ROUTE_PREFIX = "/projects/[projectId]/environments/[environmentId]";

export interface ProjectNavHistoryEntry extends Record<string, unknown> {
  /** The path without query or hash; one entry per page however its tabs or filters change. */
  routeKey: string;
  /** The URL last visited for this page, so a link returns to the same tab and filters. */
  href: string;
  label: string;
  icon?: IconName;
  /** The object the page shows, when the entity-preview registry recognizes its route. */
  entity?: { kind: EntityKind; id: string };
  visitedAtMs: number;
}

interface ProjectNavHistoryStore extends Record<string, unknown> {
  entriesByEnvironment: Record<string, ProjectNavHistoryEntry[]>;
}

const EMPTY_HISTORY_STORE: ProjectNavHistoryStore = { entriesByEnvironment: {} };

/**
 * The category nav's Recent section is switched off for now. This one switch removes the section
 * and stops the visit recording that feeds it; Pinned keeps showing pins made while it was on.
 */
export const PROJECT_NAV_RECENT_ENABLED = false;

/**
 * History records detail pages: environment routes with a dynamic segment of their own, such as
 * a resolver, a query run, a notebook, or a dashboard. List pages are one click away in the nav.
 */
export const isProjectNavHistoryRoute = (pathname: string): boolean => {
  if (!pathname.startsWith(`${ENVIRONMENT_ROUTE_PREFIX}/`)) return false;

  return pathname.slice(ENVIRONMENT_ROUTE_PREFIX.length).includes("[");
};

/** Moves the entry for this page to the front, replacing any older visit, and caps the list. */
export const recordProjectNavHistoryEntry = (
  entries: readonly ProjectNavHistoryEntry[],
  entry: ProjectNavHistoryEntry,
  limit: number,
): ProjectNavHistoryEntry[] => [
  entry,
  ...entries.filter((existing) => existing.routeKey !== entry.routeKey).slice(0, limit - 1),
];

const getEnvironmentKey = (projectId: string, environmentId: string) =>
  `${projectId}:${environmentId}`;

// TEMPORARY (nav concept debugging): logs History recording and reads on localhost.
export const logProjectNavRecent = (event: string, detail?: unknown) => {
  if (typeof window !== "undefined" && window.location.hostname === "localhost") {
    console.info(`[nav-recent] ${event}`, detail ?? "");
  }
};

/**
 * Records the current page in History once its label resolves. Labels that arrive late (a
 * route param replaced by a fetched name) overwrite the entry in place.
 */
export const useRecordProjectNavHistory = ({
  enabled,
  label,
  icon,
}: {
  /** False where nothing reads History (the classic nav), so no visits are stored. */
  enabled: boolean;
  label: string | undefined;
  icon: IconName | undefined;
}) => {
  const router = useRouter();
  const { projectId, environmentId } = useRouteAndTeamInfo();
  const [, setStore] = useLocalStorageSyncedState(
    LocalStorageKey.PROJECT_NAV_HISTORY,
    EMPTY_HISTORY_STORE,
  );
  const recordable =
    enabled &&
    router.isReady &&
    projectId != null &&
    environmentId != null &&
    label != null &&
    label.length > 0 &&
    isProjectNavHistoryRoute(router.pathname);

  useEffect(() => {
    if (!enabled) return;
    logProjectNavRecent("record check", {
      recordable,
      isReady: router.isReady,
      projectId,
      environmentId,
      label,
      pathname: router.pathname,
      historyRoute: isProjectNavHistoryRoute(router.pathname),
    });
    if (!recordable) return;

    const environmentKey = getEnvironmentKey(projectId, environmentId);
    const entry: ProjectNavHistoryEntry = {
      routeKey: getProjectNavigationRouteKey(router.asPath),
      href: router.asPath,
      label,
      icon,
      entity: matchEntityRoute(router.pathname, router.query),
      visitedAtMs: Date.now(),
    };
    logProjectNavRecent("record write", entry);
    setStore((store) => {
      const current = store?.entriesByEnvironment ?? {};
      return {
        entriesByEnvironment: {
          ...current,
          [environmentKey]: recordProjectNavHistoryEntry(
            current[environmentKey] ?? [],
            entry,
            PROJECT_NAV_HISTORY_STORED_LIMIT,
          ),
        },
      };
    });
  }, [
    enabled,
    environmentId,
    icon,
    label,
    projectId,
    recordable,
    router.asPath,
    router.isReady,
    router.pathname,
    router.query,
    setStore,
  ]);
};

/** Detail pages visited in this environment, most recent first. */
export const useProjectNavHistoryEntries = (): readonly ProjectNavHistoryEntry[] => {
  const { projectId, environmentId } = useRouteAndTeamInfo();
  const [store] = useLocalStorageSyncedState(
    LocalStorageKey.PROJECT_NAV_HISTORY,
    EMPTY_HISTORY_STORE,
  );

  return useMemo(() => {
    if (projectId == null || environmentId == null) return [];

    return store.entriesByEnvironment?.[getEnvironmentKey(projectId, environmentId)] ?? [];
  }, [environmentId, projectId, store]);
};
